import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDrawer } from '../context/DrawerContext';
import { useCurrentPath } from '../context/CurrentPathContext';
import { CLOSE_METHOD } from '../utils/trackEvent';
import NavLink from './NavLink';
import SubMenuTrigger from './SubMenuTrigger';
import DrawerNavFragment from './DrawerNavFragment';

import {
	safeNavItems,
	isNavItemActive,
	hasActiveDescendant,
	isFragmentWrapper,
	isFragmentItem
} from '../utils/navItemUtils';

/**
 * Determines the variant key for a nav item.
 *
 * @param {NavigationItem} item
 * @returns {NavItemVariant}
 */
const getVariant = (item) => {
	if (isFragmentItem(item)) return 'fragment';
	return safeNavItems(item).length > 0 ? 'drilldown' : 'link';
};

/** Maps each variant key to the component responsible for rendering it. */
const variantMap = {
	link: NavLink,
	drilldown: SubMenuTrigger,
	fragment: DrawerNavFragment
};

/** CSS class applied to nav item labels in the main panel. */
const LABEL_HEADING_CLASS = 'h4';

/**
 * NavItem — orchestrator that selects the correct variant component.
 * Only passes variant-specific props (ISP).
 *
 * @param {Object} props
 * @param {NavigationItem} props.item
 * @param {(item: NavigationItem, triggerRef?: React.RefObject) => void} props.onSubmenuOpen - Called when a sub-menu trigger is clicked
 * @param {string|null} props.resolvedActiveSectionId - ID of the single section
 *   that matched via path-only fallback (direct navigation without _ddc_nav_drawer).
 *   Null when the URL already contains _ddc_nav_drawer or when zero/multiple
 *   sections matched by path. Computed once at NavItemList level so the
 *   "exactly one match" constraint can be enforced across all siblings.
 */
const NavItem = ({ item, onSubmenuOpen, resolvedActiveSectionId }) => {
	const { closeDrawer } = useDrawer();
	const currentPath = useCurrentPath();
	const variant = getVariant(item);
	const NavItemComponent = variantMap[variant];

	// Following a nav link auto-closes the drawer; attribute that close to the
	// link rather than letting it fall back to the close-button default.
	const closeOnNavLink = useCallback(
		() => closeDrawer(CLOSE_METHOD.NAV_LINK),
		[closeDrawer]
	);

	/**
	 * Computes active state for link/drilldown variants.
	 * Not called for fragment variants — fragments have no active state.
	 */
	const getActiveState = () => {
		const directMatch = isNavItemActive(item, currentPath);
		const isActive =
			resolvedActiveSectionId !== null
				? resolvedActiveSectionId === item.id
				: directMatch || hasActiveDescendant(item, currentPath);
		return { isActive, isLeafActive: directMatch };
	};

	// Maps each variant key to the props its component expects.
	const variantPropsMap = {
		// Unreachable from NavItemList, which filters every fragment item out of
		// the top level, so no `zonePosition` is plumbed through here — see the
		// note on that filter. Kept because getVariant can still name the variant.
		fragment: () => ({
			url: isFragmentWrapper(item)
				? safeNavItems(item).find((c) => c.embedded)?.url ?? ''
				: item.url
		}),
		link: () => {
			const { isActive, isLeafActive } = getActiveState();
			return {
				item,
				onLinkClick: closeOnNavLink,
				isActive,
				isLeafActive,
				labelClassName: LABEL_HEADING_CLASS
			};
		},
		drilldown: () => {
			const { isActive, isLeafActive } = getActiveState();
			return { item, onSubmenuOpen, isActive, isLeafActive };
		}
	};
	const variantProps = variantPropsMap[variant]?.() ?? {};

	const wrapperClassName =
		variant === 'fragment' ? 'ws-nav-drawer__fragment' : undefined;

	return (
		<li className={wrapperClassName}>
			<NavItemComponent {...variantProps} />
		</li>
	);
};

NavItem.propTypes = {
	item: PropTypes.shape({
		id: PropTypes.string,
		label: PropTypes.string.isRequired,
		url: PropTypes.string,
		target: PropTypes.string,
		navigationItems: PropTypes.arrayOf(PropTypes.shape({})),
		embedded: PropTypes.bool,
		selected: PropTypes.bool,
		matched: PropTypes.bool
	}).isRequired,
	onSubmenuOpen: PropTypes.func.isRequired,
	resolvedActiveSectionId: PropTypes.string
};

NavItem.defaultProps = {
	resolvedActiveSectionId: null
};

export default NavItem;
